import React, { useState } from 'react';
import { getIntegrationsByCategory, IntegrationConfigs } from '@/config/integrationConfigs';

/**
 * IntegrationSelector - Helps users select a predefined integration template
 * 
 * Shows available integrations grouped by category with descriptions.
 * When selected, pre-populates the secret form with appropriate defaults.
 * 
 * @param {Function} onSelect - Callback when an integration is selected
 * @param {Function} onCancel - Callback when user cancels selection
 */
const IntegrationSelector = ({ onSelect, onCancel }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const integrationsByCategory = getIntegrationsByCategory();

    /**
     * Filter integrations based on search query
     */
    const filteredCategories = {};
    Object.entries(integrationsByCategory).forEach(([category, integrations]) => {
        const filtered = integrations.filter(integration => {
            const searchLower = searchQuery.toLowerCase();
            return (
                integration.name.toLowerCase().includes(searchLower) ||
                integration.description.toLowerCase().includes(searchLower) ||
                integration.key.toLowerCase().includes(searchLower)
            );
        });
        if (filtered.length > 0) {
            filteredCategories[category] = filtered;
        }
    });

    /**
     * Handle integration selection
     */
    const handleSelect = (integrationKey, integration) => {
        // Pre-populate form with integration defaults
        const defaults = {
            secretKey: integrationKey.charAt(0).toUpperCase() + integrationKey.slice(1),
            displayName: integration.name,
            description: integration.description,
            secretType: integration.secretType,
            category: integration.category === 'IT Systems' ? 'IT' : 
                      integration.category === 'External APIs' ? 'External API' :
                      integration.category === 'Azure & Cloud' ? 'Azure' :
                      integration.category === 'Email & Messaging' ? 'IT' :
                      integration.category,
            isActive: true
        };
        onSelect(integrationKey, defaults);
    };

    return (
        <div className="integration-selector-overlay">
            <div className="integration-selector-modal">
                <div className="modal-header">
                    <h2>Choose Integration Type</h2>
                    <button 
                        className="close-button"
                        onClick={onCancel}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="modal-body">
                    <div className="search-box" style={{ marginBottom: '20px' }}>
                        <input
                            type="text"
                            placeholder="Search integrations..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '14px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>

                    <div className="integration-categories" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {Object.keys(filteredCategories).length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                No integrations found matching "{searchQuery}"
                            </div>
                        )}

                        {Object.entries(filteredCategories).map(([category, integrations]) => (
                            <div key={category} className="integration-category" style={{ marginBottom: '24px' }}>
                                <h3 style={{ 
                                    color: '#0066cc', 
                                    fontSize: '16px', 
                                    marginBottom: '12px',
                                    borderBottom: '2px solid #0066cc',
                                    paddingBottom: '6px'
                                }}>
                                    {category}
                                </h3>
                                <div className="integration-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '12px'
                                }}>
                                    {integrations.map(integration => (
                                        <div
                                            key={integration.key}
                                            className="integration-card"
                                            onClick={() => handleSelect(integration.key, integration)}
                                            style={{
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                padding: '16px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                backgroundColor: '#fff'
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.borderColor = '#0066cc';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,102,204,0.2)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.borderColor = '#ddd';
                                                e.currentTarget.style.boxShadow = 'none';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <div style={{ 
                                                fontWeight: 'bold', 
                                                marginBottom: '6px',
                                                color: '#333',
                                                fontSize: '14px'
                                            }}>
                                                {integration.name}
                                            </div>
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: '#666',
                                                marginBottom: '8px',
                                                lineHeight: '1.4'
                                            }}>
                                                {integration.description}
                                            </div>
                                            <div style={{ 
                                                fontSize: '11px', 
                                                color: '#0066cc',
                                                fontWeight: '500'
                                            }}>
                                                {integration.secretType}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-footer" style={{
                    borderTop: '1px solid #ddd',
                    padding: '16px',
                    textAlign: 'right'
                }}>
                    <button
                        onClick={onCancel}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '8px'
                        }}
                    >
                        Skip - Create Generic Secret
                    </button>
                </div>
            </div>

            <style>{`
                .integration-selector-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                }

                .integration-selector-modal {
                    background: white;
                    border-radius: 8px;
                    width: 90%;
                    max-width: 900px;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                }

                .modal-header {
                    padding: 20px;
                    border-bottom: 1px solid #ddd;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 20px;
                    color: #333;
                }

                .close-button {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #666;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-button:hover {
                    color: #000;
                }

                .modal-body {
                    padding: 20px;
                    flex: 1;
                    overflow: auto;
                }
            `}</style>
        </div>
    );
};

export default IntegrationSelector;
